import axios, { AxiosError } from 'axios'
import { supabase } from '@/lib/supabase'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
})

// Cache for session to avoid repeated blocking calls
let sessionCache: { session: any; timestamp: number } | null = null
const SESSION_CACHE_TTL = 30000 // 30 seconds - more frequent refresh

// Function to update cache (exported for use in authStore)
export function updateSessionCache(session: any) {
  if (session) {
    sessionCache = { session, timestamp: Date.now() }
  } else {
    sessionCache = null
  }
}

// Listen to auth state changes to keep cache updated
supabase.auth.onAuthStateChange((event, session) => {
  console.log('🔄 Auth state changed:', event, session ? 'session exists' : 'no session')
  updateSessionCache(session)

  // Clear cache on sign out
  if (event === 'SIGNED_OUT') {
    sessionCache = null
  }
})

// Request interceptor for auth token from Supabase
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Check if we need to refresh the session
      const needsRefresh = !sessionCache ||
        Date.now() - sessionCache.timestamp >= SESSION_CACHE_TTL ||
        (sessionCache.session?.expires_at &&
         sessionCache.session.expires_at * 1000 - Date.now() < 60000) // Refresh if expires in < 1 minute

      if (needsRefresh) {
        // Force refresh the session
        try {
          const sessionPromise = supabase.auth.getSession()
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Session timeout')), 3000) // 3 second timeout
          )

          const sessionResult = await Promise.race([sessionPromise, timeoutPromise]) as any

          if (sessionResult?.data?.session) {
            sessionCache = {
              session: sessionResult.data.session,
              timestamp: Date.now()
            }
          } else {
            // No valid session
            sessionCache = null
          }
        } catch (sessionError) {
          console.warn('⚠️ Failed to refresh session:', sessionError)
          sessionCache = null
        }
      }

      // Use cached session if available
      if (sessionCache?.session?.access_token) {
        config.headers.Authorization = `Bearer ${sessionCache.session.access_token}`
      }

      return config
    } catch (error) {
      console.error('❌ Request interceptor error:', error)
      return config
    }
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log('📥 API Response:', { status: response.status, url: response.config.url, data: response.data })
    return response
  },
  async (error: AxiosError) => {
    console.error('❌ API Error:', {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data,
    })

    if (error.response?.status === 401 && error.config && !(error.config as any)._retry) {
      console.log('🔄 Token expired, attempting refresh...')

      // Mark this request as retried
      ;(error.config as any)._retry = true

      try {
        // Force refresh the session
        const { data, error: refreshError } = await supabase.auth.refreshSession()

        if (refreshError) {
          throw refreshError
        }

        if (data.session?.access_token) {
          console.log('✅ Token refreshed successfully')

          // Update cache
          sessionCache = {
            session: data.session,
            timestamp: Date.now()
          }

          // Update the Authorization header and retry the request
          error.config.headers.Authorization = `Bearer ${data.session.access_token}`
          return apiClient(error.config)
        }
      } catch (refreshError) {
        console.error('❌ Failed to refresh token:', refreshError)
      }

      // If refresh failed, sign out
      console.log('🔒 Token refresh failed, signing out...')
      await supabase.auth.signOut()
      window.location.href = '/auth/login'
      return Promise.reject(error)
    }

    // Extract error message from response
    // Backend returns { success: false, error: { message: '...', code: '...' } } or { message: '...' }
    const responseData = error.response?.data as any
    const errorMessage =
      responseData?.error?.message ||
      responseData?.message ||
      error.message ||
      'An error occurred'

    console.log('📝 Error message extracted:', errorMessage)

    // Create a more informative error (attach response data for callers that need details, e.g. compilation errors)
    const apiError: Error = new Error(errorMessage)
    ;(apiError as any).status = error.response?.status
    ;(apiError as any).code = error.response?.status
    ;(apiError as any).responseData = error.response?.data

    return Promise.reject(apiError)
  }
)

export default apiClient
